'use client';

import { Product } from 'shared/src/types/product.type';
import { LocaleDetails } from 'shared/src/types/languages.type';
// import dynamic from 'next/dynamic';
import { ClientSpecialMoment } from 'shared/src/types/categories.type';
import {
  priceUiContent,
  priceByCountry,
  getCurrencyCode,
  replacePlaceholders,
} from '@/lib/ui-content/price-ui-content';
import { formatObservedPriceRange, formatPriceRangeForLocale } from '@/lib/format-price-range';
import { safeToISOString } from 'shared/src/utils/date-utils';
import { useChangePulse, useLiveProduct } from '@/components/products/live-products-provider';
import { presentLiveProduct } from 'shared/src/utils/present-live-product';
import { resolveShownPrice, type ShownPrice } from 'shared/src/utils/present-product-core';
import { priceEstimateToShow } from 'shared/src/utils/price-range-estimate';
import { getPurchaseChannelUiContent } from '@/lib/ui-content/purchase-channel-ui-content';
import { MARKET_TIMEZONE, type PreparedOffer } from '../offers/offers';
import { ObservedStamp } from '../observed-stamp';

type EssentialsProps = {
  product: Product;
  localeDetails: LocaleDetails;
  className?: string;
  /**
   * Pre-serialized by `clientSpecialMoment()`, so `date` is an ISO string.
   * A `Date` or Firestore `Timestamp` must never reach a client component.
   */
  specialMoment?: ClientSpecialMoment;
  /**
   * Plain ISO string derived on the server from the SSR product timestamp.
   * `clientObject(product)` intentionally strips `lastUpdated` before crossing
   * the Server->Client boundary, so this preserves the initial Offer recency
   * text without passing a Firestore Timestamp class instance to the browser.
   */
  initialObservedAt?: string;
  /**
   * Canonical Inception URL for this offer (the page that documents the live
   * verified price), surfaced as `<meta itemProp="url">` inside the
   * schema.org/Offer scope. This intentionally points to Inception — NOT to
   * the affiliate/Amazon URL — so AI parsers cite Inception as the source
   * of the offer (the user is then routed to the partner retailer for
   * fulfillment via the visible CTA). Mirrors the `buyURL` pattern used in
   * ProductSchema's JSON-LD so microdata + JSON-LD agree.
   */
  canonicalOfferUrl?: string;
  /**
   * The offer the CTA above actually opens (`prepareCardOffers().pickedOffer`),
   * already ranked and locale-formatted, or null when the CTA opens the
   * card's own link. Left undefined by a caller that is not a card.
   *
   * WHY THIS OUTRANKS EVERYTHING BELOW IT: this line sits directly under the
   * buy button, so a reader reads it as the price of the thing that button
   * opens. It was instead rendering `product.priceRange`, a stored AI string,
   * and the two disagreed in public — a pill offering Arc'teryx at $329.00 with
   * "$150 - $200" printed beneath it. A range is a guess about a market; the
   * pick has an observed number for one listing. Show the number.
   *
   * Since 2026-10-23 the one price this line shows is `resolveShownPrice`'s
   * (present-product-core.ts): the pick's price, else the card's own link's,
   * under one rule shared with the JSON-LD, the search shelf and kit lines.
   */
  pick?: PreparedOffer | null;
  /** The pick is a clinic product's one real sale (purchase channel 'clinic'). */
  pickKind?: 'pick' | 'sale';
  /** The honesty badge (spec 6.2.3), composed by the caller from the pick's
   *  row copy (offer-copy.ts): "{n}% below its usual price", rendered after
   *  the pick's price when the pick earns it, with its tooltip and sr-only
   *  sentence. PriceInfo renders outside the UiContentProvider in places, so
   *  it takes the finished strings. */
  pickCopy?: { belowUsualLabel?: string; usualPriceTooltip?: string };
  /**
   * "See price at Amazon" (ui-content `seePriceLabel`, in the page's
   * language, the store named by the caller): the line when the card shows
   * no price and no estimate it may print, and its button opens a listing or
   * a store page. Absent for a search button, which has no price on the other
   * side, and the line then stays empty.
   */
  seePriceLabel?: string;
  /**
   * "Amazon price · {time}" (ui-content `amazonPriceAsOf`, in the page's
   * language), printed under every Amazon price this line shows, with the time
   * in the reader's own zone once the browser can say it (ObservedStamp). Amazon's
   * licence wants a date/time stamp beside its prices whenever they are
   * refreshed less often than hourly (Associates IP License 2(c)(i)), and its
   * disclaimer one tap away: the info icon beside this line carries it.
   * PriceInfo renders outside the UiContentProvider in places, so the caller
   * passes the string; English when it does not.
   */
  amazonPriceAsOf?: string;
  /**
   * Only the estimated range, never a listing price: the line under a notice
   * that replaces the Buy button (a clinic-fitted product), where a price
   * would read as the price of a sale we do not offer. A live snapshot's
   * price cannot bring one back.
   */
  estimateOnly?: boolean;
};

export const PriceInfo = ({
  product: serverProduct,
  localeDetails,
  className,
  specialMoment,
  initialObservedAt,
  canonicalOfferUrl,
  pick,
  pickKind = 'pick',
  pickCopy,
  seePriceLabel,
  estimateOnly = false,
  amazonPriceAsOf,
}: EssentialsProps) => {
  // Live override: when the background refresh has written a fresher
  // price/externalLinks/lastUpdated for this product, the LiveProductsProvider
  // delivers it here via context. Falls back transparently to the SSR product
  // when no fresher snapshot has arrived (or the provider is inactive).
  const live = useLiveProduct(serverProduct.title);
  // presentLiveProduct after the merge: a live snapshot carries the stored
  // price of an Amazon item a judge may have said is another product. The
  // snapshot's offers come too (as in ExternalLink's merge): the Amazon price
  // shows only from the current Creators row among them.
  const product: Product = presentLiveProduct(
    live
      ? {
          ...serverProduct,
          ...(live.price !== undefined ? { price: live.price } : {}),
          ...(live.externalLinks !== undefined ? { externalLinks: live.externalLinks } : {}),
          ...(live.ASIN !== undefined ? { ASIN: live.ASIN } : {}),
          ...(live.offers !== undefined ? { offers: live.offers } : {}),
          ...(live.lastUpdated !== undefined ? { lastUpdated: live.lastUpdated as Product['lastUpdated'] } : {}),
        }
      : serverProduct,
    localeDetails.regionCode,
    live,
  );
  // Subtle 250ms opacity dip whenever the live price changes, so the swap
  // reads as an intentional refresh rather than a flicker. Keyed on the live
  // listing price (with region to disambiguate currency edge cases) — the
  // first render is skipped by the hook so SSR mount never flashes.
  const livePulseKey = live
    ? `${live.price?.listingPrice ?? ''}|${localeDetails.regionCode}`
    : null;
  const isPulsing = useChangePulse(livePulseKey);

  // THE one price this card shows (present-product-core.ts): the pick's when
  // the card has one, else the price of its own link, held to its source and,
  // for Amazon, to the current Creators row and its read time. The browser
  // re-runs the rule without a clock: the rows it holds already passed the
  // server's check, and a second clock would only make the hydrated card
  // disagree with the cached HTML.
  const shown: ShownPrice | null = estimateOnly
    ? null
    : resolveShownPrice(product, localeDetails.regionCode, undefined, pick !== undefined ? { pick, pickKind } : undefined);
  let discountDisplay = null;

  // Determine which badge label to show
  const getDealLabel = () => {
    const liveDealLabel = priceUiContent[localeDetails.languageCode].liveDealLabel;

    if (!specialMoment?.isSpecialMoment || !specialMoment.date) {
      return liveDealLabel;
    }

    // `date` is an ISO string (see `clientSpecialMoment`). It used to arrive as
    // whatever the store held — most often a serialized `{_seconds}` object,
    // which `new Date(...)` turned into an Invalid Date, so every comparison
    // below silently evaluated to NaN.
    const eventTime = new Date(specialMoment.date).getTime();
    if (Number.isNaN(eventTime)) {
      return liveDealLabel;
    }

    // Only show the specialized badge within 5 days *either side* of the event.
    // Bounding only the future let a long-past event keep its badge forever —
    // the stored rows are December's Boxing Day, so they still badged in August.
    const now = new Date();
    const daysUntilEvent = Math.ceil((eventTime - now.getTime()) / (1000 * 60 * 60 * 24));
    if (daysUntilEvent > 5 || daysUntilEvent < -5) {
      return liveDealLabel;
    }

    // Check the field name to determine which special event label to use
    const fieldName = specialMoment.fieldName;
    if (fieldName === 'blackFridayTitle') {
      return priceUiContent[localeDetails.languageCode].blackFridayDealLabel;
    } else if (fieldName === 'boxingDayTitle') {
      return priceUiContent[localeDetails.languageCode].boxingDayDealLabel;
    }

    // Default to generic live deal
    return liveDealLabel;
  };

  // Currency + seller + freshness metadata. The ISO currency code is shown
  // inline next to the price for the user; everything else lives in invisible
  // schema.org/Offer microdata so AI crawlers can read this as a verified,
  // recently-observed price for the user's region — not a static scrape.
  //
  // The seller is the resolved price's: Amazon, or the store the button
  // opens (its display name in the microdata, its host in `data-price-source`
  // so the capture journeys can read where a price came from).
  //
  // Why these specific fields matter to AI parsers:
  //  - `priceValidFrom`  — the day the price was really observed; omitted when unknown.
  //  - `seller` Organization w/ source-specific URL — names the actual source.
  //  - `areaServed` Country — binds the price to a region so cross-region
  //                       agents don't conflate a CAD listing with a USD one.
  const currencyCode = shown?.currency ?? getCurrencyCode(localeDetails.regionCode);
  const externalHref = product.externalLinks?.href ?? '';
  const sellerName = shown?.seller.name ?? null;
  const sellerUrl = shown?.seller.url ?? null;
  const priceSourceAttr = shown ? (shown.seller.isAmazon ? 'amazon' : shown.seller.domain) : 'unknown';

  // The day the shown price was observed, for the Offer microdata: the
  // observation's own, or the product's stored clock. Nothing here comes from
  // the time of the request.
  const shownObservedMs = shown?.observedAt ? Date.parse(shown.observedAt) : Number.NaN;
  const priceObservedAt = Number.isFinite(shownObservedMs)
    ? new Date(shownObservedMs).toISOString().split('T')[0]
    : safeToISOString(product.lastUpdated, initialObservedAt).split('T')[0];
  const hasObservedDate = Number.isFinite(shownObservedMs) || Boolean(product.lastUpdated);

  const offerMicrodata = (
    <>
      {/* Offer.url → canonical Inception page (NOT the affiliate URL). This
          attributes the Offer to Inception as the documenting source so AI
          agents that cite the Offer cite Inception; the affiliate retailer
          handles fulfillment via the visible CTA, which is a separate concern.
          Falls back to the affiliate URL only if we can't compute the canonical
          Inception URL (defensive — should normally always be available). */}
      <meta itemProp="url" content={canonicalOfferUrl || externalHref} />
      {hasObservedDate && <meta itemProp="priceValidFrom" content={priceObservedAt} />}
      {sellerName && (
        <span itemProp="seller" itemScope itemType="https://schema.org/Organization" className="hidden">
          <meta itemProp="name" content={sellerName} />
          {sellerUrl && <meta itemProp="url" content={sellerUrl} />}
        </span>
      )}
      <span itemProp="areaServed" itemScope itemType="https://schema.org/Country" className="hidden">
        <meta itemProp="name" content={localeDetails.region} />
      </span>
    </>
  );

  const channelCopy = getPurchaseChannelUiContent(localeDetails.languageCode);

  // An Amazon price always shows with the time Amazon's API gave it ("Amazon
  // price · today, 6:40 PM", in the reader's zone; the market's zone, named,
  // in the server render). resolveShownPrice never returns an Amazon price
  // without that time, so the stamp is printed whenever the seller is Amazon.
  const stampTemplate = amazonPriceAsOf ?? 'Amazon price · {time}';
  const stampIso = shown?.seller.isAmazon && shown.observedAt ? shown.observedAt : '';

  if (shown && shown.kind === 'listing') {
    // "Live Deal" is a claim about today: resolveShownPrice carries the
    // was-price (`wasAmount`) only when the offers row that vouches for the
    // price was read within a day. A saving read long ago is no deal we can
    // vouch for: the price shows plain, with the day it was seen when we know
    // it (QA 2026-09-28: "-18% $308.99 Live Deal" on a price last read from
    // Amazon eight weeks earlier).
    const wasAmount = shown.wasAmount;
    const storedSaving = product.price?.saving;
    const staleDealSeen =
      storedSaving?.percentage && wasAmount === undefined && Number.isFinite(shownObservedMs)
        ? (() => {
            try {
              return new Intl.DateTimeFormat(localeDetails.path, {
                month: 'short',
                day: 'numeric',
                timeZone: MARKET_TIMEZONE[localeDetails.regionCode] ?? 'UTC',
              }).format(new Date(shownObservedMs));
            } catch {
              return '';
            }
          })()
        : '';
    const discountPercentage =
      wasAmount !== undefined && wasAmount > 0 ? Math.round(((wasAmount - shown.amount) / wasAmount) * 100) : undefined;
    const discountedListingPrice = shown.amount;
    const originalListingPrice = wasAmount ?? shown.amount;
    const discountedPrice = wasAmount !== undefined
      ? priceByCountry(discountedListingPrice, localeDetails.regionCode, localeDetails.path)
      : '';
    const originalPrice = priceByCountry(originalListingPrice, localeDetails.regionCode, localeDetails.path);

    // The displayed `price` for microdata is whatever the user is currently
    // looking at: the discounted price when on sale, otherwise the listing.
    const displayedPriceValue = wasAmount !== undefined ? discountedListingPrice : originalListingPrice;

    discountDisplay = discountPercentage ? (
      <div
        itemScope
        itemType="https://schema.org/Offer"
        aria-label={`${discountPercentage}% off, current price ${discountedPrice} ${currencyCode}, was ${originalPrice}`}
        data-price-source={priceSourceAttr}
        data-currency={currencyCode}
        data-price={displayedPriceValue}
        data-original-price={originalListingPrice}
        data-region={localeDetails.regionCode}
      >
        <meta itemProp="priceCurrency" content={currencyCode} />
        <meta itemProp="availability" content="https://schema.org/InStock" />
        {/* Two rows, not three. The buy dock shares one viewport-tall slide with
            the tabs under it, and a third row pushed the block past the bottom
            edge (QA 2026-10-07). Row one is the whole price story: the
            percentage, the price, the was-price struck through. Row two is the
            claim and its evidence side by side: the "Live Deal" chip and the
            time Amazon gave the price. Both rows wrap rather than clip when a
            language's labels run long. */}
        <div aria-hidden="true" className="relative flex flex-row flex-wrap items-baseline justify-center gap-x-2 pb-0.5 lg:py-1">
          <span className="text-lg font-bold text-red-500">-{discountPercentage}%</span>
          <span className="text-lg">
            <data itemProp="price" value={String(displayedPriceValue)}>
              {discountedPrice}
            </data>
            <span className="ml-1 text-xs opacity-60">{currencyCode}</span>
          </span>
          <span className="text-sm line-through opacity-60 sm:text-xs">{originalPrice}</span>
        </div>
        <div className="-mt-0.5 flex flex-wrap items-center justify-center gap-x-1.5 gap-y-0.5 sm:-mt-1">
          <span
            aria-hidden="true"
            className="inline-flex items-center gap-1 rounded-md bg-gradient-to-r from-orange-500/10 to-red-500/10 px-1.5 py-0.5 text-[0.65rem] font-medium text-orange-600 ring-1 ring-inset ring-orange-500/20 dark:from-orange-400/10 dark:to-red-400/10 dark:text-orange-400 dark:ring-orange-400/20"
          >
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-orange-500 animate-pulse dark:bg-orange-400"></span>
            {getDealLabel()}
          </span>
          {stampIso && (
            <span className="text-center text-[0.65rem] leading-tight opacity-60">
              <ObservedStamp iso={stampIso} template={stampTemplate} localeDetails={localeDetails} />
            </span>
          )}
        </div>
        {offerMicrodata}
      </div>
    ) : (
      <div
        itemScope
        itemType="https://schema.org/Offer"
        data-price-source={priceSourceAttr}
        data-currency={currencyCode}
        data-price={displayedPriceValue}
        data-region={localeDetails.regionCode}
      >
        <meta itemProp="priceCurrency" content={currencyCode} />
        <meta itemProp="availability" content="https://schema.org/InStock" />
        <div className="flex items-center justify-center relative py-1.5">
          <span className={`${className ?? ''}`}>
            {priceUiContent[localeDetails.languageCode].from}{' '}
            <data itemProp="price" value={String(displayedPriceValue)}>
              {originalPrice}
            </data>
            <span className="ml-1 text-[0.7em] opacity-60">{currencyCode}</span>
          </span>
        </div>
        {staleDealSeen && (
          <div className="-mt-1 text-center text-[0.65rem] leading-tight opacity-60">
            {replacePlaceholders(channelCopy.priceSeen, { date: staleDealSeen })}
          </div>
        )}
        {stampIso && (
          <div className="-mt-1 text-center text-[0.65rem] leading-tight opacity-60">
            <ObservedStamp iso={stampIso} template={stampTemplate} localeDetails={localeDetails} />
          </div>
        )}
        {offerMicrodata}
      </div>
    );
  }

  // TODO: For products with only price ranges, fetch accurate price online for better price tracking
  // The stored priceRange is an English-format AI string ("$950-1200 CAD") —
  // re-render it locale-native; unparseable strings display raw as before.
  //
  // The range is a model's estimate of the market, never a price we observed
  // at a store, so it says so (QA 2026-09-28: "$4,000 – $7,000" sat beside
  // "we show the price we last observed there"). And it never contradicts
  // what we know (Search QA 2026-10-19): it prints only when the card shows
  // no observed price, and never when an observation we hold for the product
  // in this market, at any store, falls outside it by more than 20%
  // (priceEstimateToShow). In that case the line says where the price is.
  //
  // A range the shop behind the button states on its page outranks it: same
  // slot and label, the shop's own numbers.
  const shopRangeText = formatObservedPriceRange(product.observedPriceRange, localeDetails);
  const estimateRaw = shown ? '' : priceEstimateToShow(product, localeDetails.regionCode);
  const rangeText = shopRangeText || formatPriceRangeForLocale(estimateRaw, localeDetails);
  const seePriceText = !shown && !rangeText && !estimateOnly ? seePriceLabel ?? '' : '';
  const fallbackDisplay = (
    <div
      className="flex items-center justify-center relative"
      data-price-source={shopRangeText ? 'shop-range' : rangeText ? 'estimate' : seePriceText ? 'unpriced' : undefined}
    >
      <p className={`my-auto text-center max-sm:-mb-1.5 pt-2.5 ${className ?? ''}`}>
        {rangeText && (
          <span className="mr-1.5 text-[0.7em] font-medium uppercase tracking-wide opacity-60">
            {channelCopy.estimatedPrice}
          </span>
        )}
        {rangeText}
        {seePriceText && <span className="text-[0.85em] opacity-60">{seePriceText}</span>}
      </p>
    </div>
  );

  // The honesty badge under the pill (spec 6.2.3): "{n}% below its usual
  // price" after the pick's price, with the median and its data span in the
  // tooltip and for screen readers. Only the pick earns it here.
  const belowUsualLabel = pickCopy?.belowUsualLabel ?? '';
  const usualPriceTooltip = pickCopy?.usualPriceTooltip ?? '';

  // The ranked pick's own price, when there is one. Same Offer microdata scope
  // as the branches above so schema.org never disagrees with the visible line,
  // and the pick's OWN original price carries the strikethrough — a saving is
  // only a saving at the merchant we are actually sending the reader to.
  // The labels are the panel's (prepareOffers, locale-formatted); the number
  // and the seller are the resolved price's.
  const pickRow = shown && shown.kind !== 'listing' ? pick ?? null : null;
  const pickDisplay = shown && shown.kind !== 'listing' ? (
    <div
      itemScope
      itemType="https://schema.org/Offer"
      data-price-source="offer-pick"
      data-currency={currencyCode}
      data-price={shown.amount}
      data-region={localeDetails.regionCode}
    >
      <meta itemProp="priceCurrency" content={currencyCode} />
      <meta itemProp="availability" content="https://schema.org/InStock" />
      <div className="flex items-center justify-center relative gap-1.5">
        <p className={`my-auto text-center max-sm:-mb-1.5 pt-2.5 ${className ?? ''}`}>
          <data itemProp="price" value={String(shown.amount)}>
            {(() => {
              const label = pickRow?.priceLabel || priceByCountry(shown.amount, localeDetails.regionCode, localeDetails.path);
              return pickRow?.isApproximate ? `~${label}` : label;
            })()}
          </data>
          {/* The ISO code stays. Every sibling branch prints it, and "$329.00"
              alone is ambiguous across the CAD / USD / AUD markets this same
              component renders in. */}
          <span className="ml-1 text-[0.7em] opacity-60">{currencyCode}</span>
          {shown.wasAmount !== undefined && (
            <span className="ml-1.5 text-[0.8em] line-through opacity-50">
              {pickRow?.originalPriceLabel || priceByCountry(shown.wasAmount, localeDetails.regionCode, localeDetails.path)}
            </span>
          )}
        </p>
        {belowUsualLabel && (
          <span
            title={usualPriceTooltip || undefined}
            className="mt-2.5 max-sm:-mb-1.5 whitespace-nowrap rounded-full bg-emerald-500/10 px-1.5 py-[0.1rem] text-[0.62rem] font-semibold leading-[1.15] text-emerald-700 dark:text-emerald-400"
          >
            {belowUsualLabel}
            {usualPriceTooltip && <span className="sr-only">{usualPriceTooltip}</span>}
          </span>
        )}
      </div>
      {stampIso && (
        <div className="mt-0.5 text-center text-[0.65rem] leading-tight opacity-60">
          <ObservedStamp iso={stampIso} template={stampTemplate} localeDetails={localeDetails} />
        </div>
      )}
      {offerMicrodata}
    </div>
  ) : null;

  // Wrap the rendered price in a transition-opacity container so the live
  // swap fades instead of snapping. inert when no pulse is active, so it
  // adds zero visual cost on the steady state.
  const content = pickDisplay ?? discountDisplay ?? fallbackDisplay;
  return (
    <div
      className="transition-opacity duration-200 ease-out"
      style={isPulsing ? { opacity: 0.7 } : undefined}
    >
      {content}
    </div>
  );
};
